import React, { useRef, useState } from "react";
import { Heart, Star, Zap, Sun, Cloud, Flame, Music, Smile, RotateCw, Maximize2 } from "lucide-react";
import { TeeGraphic } from "@/components/TeeGraphic";

export const graphics = { None: null, Heart, Star, Zap, Sun, Cloud, Flame, Music, Smile };

export const DEFAULT_GRAPHIC_POS = { x: 50, y: 40 };
export const DEFAULT_TEXT_POS = { x: 50, y: 66 };

export default function DesignPreview({ custom, className = "", compact = false, editable = false, onChange }) {
  const boxRef = useRef(null);
  const [selected, setSelected] = useState(null);

  const G = custom?.graphic && custom.graphic !== "None" ? graphics[custom.graphic] : null;
  const artImage = custom?.image || null;
  const showIcon = !artImage && G;
  const hasText = !compact && !!custom?.text;

  const gPos = custom?.graphicPos || DEFAULT_GRAPHIC_POS;
  const tPos = custom?.textPos || DEFAULT_TEXT_POS;
  const gRot = custom?.graphicRot || 0;
  const tRot = custom?.textRot || 0;
  const gScale = custom?.graphicScale || 1;
  const tScale = custom?.textScale || 1;

  const graphicSize = `${(compact ? 58 : 42) * gScale}cqi`;
  const fontSize = `${((custom?.textSize || 24) / 40) * 26 * tScale}cqi`;

  const centerPx = (pos) => {
    const rect = boxRef.current.getBoundingClientRect();
    return { cx: rect.left + (pos.x / 100) * rect.width, cy: rect.top + (pos.y / 100) * rect.height, rect };
  };

  const listen = (move) => {
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const startDrag = (which) => (e) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    setSelected(which);
    listen((ev) => {
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = Math.max(0, Math.min(100, ((ev.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((ev.clientY - rect.top) / rect.height) * 100));
      onChange?.(which === "graphic" ? { graphicPos: { x, y } } : { textPos: { x, y } });
    });
  };

  const startRotate = (which) => (e) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = which === "graphic" ? gPos : tPos;
    const startRot = which === "graphic" ? gRot : tRot;
    const { cx, cy } = centerPx(pos);
    const startAngle = (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
    listen((ev) => {
      const ang = (Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180) / Math.PI;
      const rot = Math.round(startRot + (ang - startAngle));
      onChange?.(which === "graphic" ? { graphicRot: rot } : { textRot: rot });
    });
  };

  const startScale = (which) => (e) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = which === "graphic" ? gPos : tPos;
    const startScaleVal = which === "graphic" ? gScale : tScale;
    const { cx, cy } = centerPx(pos);
    const startDist = Math.hypot(e.clientX - cx, e.clientY - cy) || 1;
    listen((ev) => {
      const dist = Math.hypot(ev.clientX - cx, ev.clientY - cy);
      const s = Math.max(0.4, Math.min(2.6, startScaleVal * (dist / startDist)));
      onChange?.(which === "graphic" ? { graphicScale: s } : { textScale: s });
    });
  };

  const Handles = ({ which }) => (
    <>
      <span className="absolute border-2 border-dashed border-ink/60 rounded pointer-events-none" style={{ inset: "-8px" }} />
      <span
        onPointerDown={startRotate(which)}
        data-testid={`rotate-${which}`}
        className="absolute grid place-items-center w-6 h-6 bg-white border-2 border-ink rounded-full cursor-grab"
        style={{ top: "-36px", left: "50%", transform: "translateX(-50%)", touchAction: "none" }}
      >
        <RotateCw className="w-3 h-3" />
      </span>
      <span
        onPointerDown={startScale(which)}
        data-testid={`scale-${which}`}
        className="absolute grid place-items-center w-6 h-6 bg-white border-2 border-ink rounded-full cursor-nwse-resize"
        style={{ bottom: "-14px", right: "-14px", touchAction: "none" }}
      >
        <Maximize2 className="w-3 h-3" />
      </span>
    </>
  );

  return (
    <div className={className} onPointerDown={() => editable && setSelected(null)}>
    <TeeGraphic color={custom?.shirtColor || "#FEF08A"} className="w-full">
      <div ref={boxRef} className="absolute inset-0">
        {(artImage || showIcon) && (
          <div className="absolute" style={{ left: `${gPos.x}%`, top: `${gPos.y}%`, transform: `translate(-50%,-50%) rotate(${gRot}deg)`, zIndex: selected === "graphic" ? 3 : 1 }}>
            <div className={`relative ${editable ? "cursor-move select-none" : ""}`} onPointerDown={startDrag("graphic")} style={{ touchAction: "none" }}>
              {artImage ? (
                <img src={artImage} alt="art" draggable={false} style={{ width: graphicSize, height: "auto", display: "block", maxWidth: "none", pointerEvents: "none" }} />
              ) : (
                <G strokeWidth={2.2} style={{ color: custom?.textColor, width: graphicSize, height: graphicSize, display: "block" }} />
              )}
              {editable && selected === "graphic" && <Handles which="graphic" />}
            </div>
          </div>
        )}
        {hasText && (
          <div className="absolute" style={{ left: `${tPos.x}%`, top: `${tPos.y}%`, transform: `translate(-50%,-50%) rotate(${tRot}deg)`, maxWidth: "100%", zIndex: selected === "text" ? 3 : 2 }}>
            <div className={`relative ${editable ? "cursor-move select-none" : ""}`} onPointerDown={startDrag("text")} style={{ touchAction: "none" }}>
              <span
                className="font-display font-bold leading-none text-center block"
                style={{ color: custom?.textColor, fontSize, width: "max-content", maxWidth: "100%", wordBreak: "break-word" }}
              >
                {custom.text}
              </span>
              {editable && selected === "text" && <Handles which="text" />}
            </div>
          </div>
        )}
      </div>
    </TeeGraphic>
    </div>
  );
}
